import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';
import { useTranslation } from 'react-i18next';
import { Button, useTheme } from '@rneui/themed';

import CalendarDatePicker from './forms/CalendarDatePicker';
import Select from 'ui/form/Select';
import TableSelectForm from './forms/TableSelectForm';
import Input from 'ui/form/Input';
import TelInput from 'ui/form/TelInput';
import CheckBoxItemDictForm from 'ui/checkbox/CheckBoxItemDictForm';
import FormStyle from 'styles/Form';
import PaymentForm from './forms/PaymentForm';
import { ScreenSizeContext } from 'context/ScreenSizeContext';
import { type ReservationInfoType } from 'types/Reservation';
import { type ReservationSettingTypeType } from 'types/ReservationSetting';
import { type ScheduleType } from 'types/Schema';
import {
  getPeopleCountsForTime,
  getTableMapForPeople,
  getTablesForPeople,
  getTimesForDate,
} from '../utils/availability';
import {
  descriptionMaxLength,
  nameMaxLength,
  phoneNumberMaxLength,
} from 'const/textLength';
import { APP_STRIPE_PUBLIC_KEY } from '@env';

interface Props {
  schedule: ScheduleType[];
  reservationSetting: ReservationSettingTypeType;
  onSubmit: (values: ReservationInfoType, actions: any) => void;
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: 24,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const buildInitialValues = (): ReservationInfoType => {
  const baseInitialValues: ReservationInfoType = {
    date: '',
    time: '',
    table: null,
    people_num: 0,
    name: '',
    email: '',
    email_confirm: '',
    tel: '',
    options: [],
    accommodation: '',
    notes: '',
  };

  if (APP_STRIPE_PUBLIC_KEY && APP_STRIPE_PUBLIC_KEY !== '') {
    baseInitialValues.payment = null;
  }

  return baseInitialValues;
};

const buildValidationSchema = (
  t: (key: string) => string
): Yup.ObjectSchema<any> => {
  const baseSchema: Record<string, Yup.AnySchema> = {
    date: Yup.date()
      .nullable()
      .required(t('date') + ' ' + t('required')),
    time: Yup.string()
      .nullable()
      .required(t('time') + ' ' + t('required')),
    people_num: Yup.string()
      .nullable()
      .required(t('peopleNumber') + ' ' + t('required')),
    table: Yup.mixed()
      .nullable()
      .required(t('table') + ' ' + t('required')),
    name: Yup.string()
      .required(t('name') + ' ' + t('required'))
      .max(nameMaxLength, t('name') + ' ' + t('tooLong')),
    email: Yup.string()
      .email(t('email') + ' ' + t('invalid'))
      .required(t('email') + ' ' + t('required'))
      .max(
        descriptionMaxLength,
        t('email') + ' ' + descriptionMaxLength + t('tooLong')
      ),
    email_confirm: Yup.string()
      .max(
        descriptionMaxLength,
        t('emailConfirmation') + ' ' + descriptionMaxLength + t('tooLong')
      )
      .oneOf(
        [Yup.ref('email'), null],
        t('emailConfirmation') + ' ' + t('match')
      )
      .required(t('emailConfirmation') + ' ' + t('required'))
      .max(
        descriptionMaxLength,
        t('emailConfirmation') + ' ' + descriptionMaxLength + t('tooLong')
      ),
    tel: Yup.string()
      .required(t('phoneNumber') + ' ' + t('required'))
      .max(
        phoneNumberMaxLength,
        t('phoneNumber') + ' ' + phoneNumberMaxLength + t('tooLong')
      ),
    accommodation: Yup.string().max(
      nameMaxLength,
      t('accommodation') + ' ' + nameMaxLength + t('tooLong')
    ),
    notes: Yup.string().max(
      descriptionMaxLength,
      t('notes') + ' ' + descriptionMaxLength + t('tooLong')
    ),
  };

  if (APP_STRIPE_PUBLIC_KEY && APP_STRIPE_PUBLIC_KEY !== '') {
    baseSchema.payment = Yup.object().required(
      t('payment') + ' ' + t('required')
    );
  }

  return Yup.object().shape(baseSchema);
};

const ReservationForm: React.FC<Props> = ({
  schedule,
  reservationSetting,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const dimensions = React.useContext(ScreenSizeContext);

  const initialValues = React.useMemo(buildInitialValues, []);
  const schema = React.useMemo(() => buildValidationSchema(t), [t]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View
        style={{
          width: dimensions.window.width > 600 ? '50%' : '80%',
          alignContent: 'center',
        }}
      >
        <Formik<ReservationInfoType>
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, isValid, values, setFieldValue }) => (
            <View>
              <CalendarDatePicker
                label={t('date')}
                title={t('chooseDate')}
                placeholder={t('chooseDate')}
                name="date"
                availableDates={schedule?.map((map) => map.date)}
                setOtherFieldValues={() => {
                  setFieldValue('time', null);
                  setFieldValue('people_num', null);
                  setFieldValue('table', null);
                }}
              />
              <Select
                label={t('time')}
                title={t('chooseTime')}
                placeholder={t('chooseTime')}
                name="time"
                selectItems={
                  schedule?.find((item) => item.date === values.date) &&
                  values?.date
                    ? getTimesForDate(schedule, values.date)
                    : []
                }
                setOtherFieldValues={() => {
                  setFieldValue('people_num', null);
                  setFieldValue('table', null);
                }}
                disabled={!schedule?.find((item) => item.date === values.date)}
              />
              <Select
                label={t('peopleNumber')}
                title={t('enterPeopleNumber')}
                placeholder={t('enterPeopleNumber')}
                name="people_num"
                selectItems={
                  schedule?.find((item) => item.date === values.date) &&
                  values?.time
                    ? getPeopleCountsForTime(schedule, values.date, values.time)
                    : []
                }
                setOtherFieldValues={() => {
                  setFieldValue('table', null);
                }}
                disabled={
                  !(
                    schedule?.find((item) => item.date === values.date) &&
                    values?.time
                  )
                }
              />
              <TableSelectForm
                name="table"
                label={t('table')}
                tableList={
                  schedule?.find((item) => item.date === values.date) &&
                  values?.time &&
                  values?.people_num
                    ? getTablesForPeople(
                        schedule,
                        values.date,
                        values.time,
                        values.people_num
                      )
                    : []
                }
                tableMapList={
                  schedule?.find((item) => item.date === values.date) &&
                  values?.time &&
                  values?.people_num
                    ? getTableMapForPeople(
                        schedule,
                        values.date,
                        values.time,
                        values.people_num
                      )
                    : []
                }
                disabled={
                  !(
                    schedule?.find((item) => item.date === values.date) &&
                    values?.time &&
                    values?.people_num
                  )
                }
              />
              <Input
                label={t('name')}
                placeholder={t('enterName')}
                name="name"
                maxLength={nameMaxLength}
                style={[FormStyle.input, { borderColor: theme.colors.primary }]}
              />
              <Input
                label={t('email')}
                placeholder={t('enterEmail')}
                name="email"
                maxLength={descriptionMaxLength}
                style={[FormStyle.email, { borderColor: theme.colors.primary }]}
              />
              <Input
                label={t('emailConfirmation')}
                placeholder={t('enterEmailConfirmation')}
                name="email_confirm"
                maxLength={descriptionMaxLength}
                style={[FormStyle.email, { borderColor: theme.colors.primary }]}
              />
              <TelInput
                label={t('phoneNumber')}
                placeholder={t('enterPhoneNumber')}
                name="tel"
                style={{
                  phoneArea: {
                    ...FormStyle.phoneArea,
                    container: {
                      borderColor: theme.colors.primary,
                    },
                  },
                }}
              />
              <CheckBoxItemDictForm
                label={t('options')}
                name="options"
                itemDict={reservationSetting}
              />
              <Input
                label={t('accommodation')}
                placeholder={t('enterAccommodation')}
                name="accommodation"
                maxLength={nameMaxLength}
                style={{
                  ...FormStyle.input,
                  borderColor: theme.colors.primary,
                }}
              />
              <Input
                label={t('notes')}
                placeholder={t('enterNotes')}
                name="notes"
                multiline={true}
                maxLength={descriptionMaxLength}
                style={[
                  FormStyle.input,
                  {
                    height: 200,
                    borderColor: theme.colors.primary,
                  },
                ]}
              />
              {APP_STRIPE_PUBLIC_KEY ? (
                <View>
                  <PaymentForm label={t('payment')} name="payment" />
                </View>
              ) : (
                <></>
              )}
              <Button
                title={t('next')}
                onPress={handleSubmit}
                disabled={!isValid}
              />
            </View>
          )}
        </Formik>
      </View>
    </ScrollView>
  );
};

export default ReservationForm;
